import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import MySchedule from "../components/MySchedule";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isAdminRole, canManageRecords, accessibleDivisionNames } from "../lib/access";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, Clock, ChevronLeft, ChevronRight, History, CalendarCheck,
  MapPin, ShieldCheck, ArrowLeft, CalendarPlus, Copy, Printer, CalendarOff, Trash2 as TrashIcon,
} from "lucide-react";
import { toast } from "sonner";
import { tzBanner, utcIsoToZonedWall, zonedWallToUtcIso, fmtTime, zonedDateKey, todayKey } from "../lib/timezone";

const STATUSES = ["Scheduled", "On Duty", "Completed", "Absent", "Open"];
const STATUS_COLOR = {
  "On Duty": "bg-emerald-100 text-emerald-700",
  "Scheduled": "bg-blue-100 text-blue-700",
  "Completed": "bg-slate-100 text-slate-600",
  "Absent": "bg-amber-100 text-amber-700",
  "Open": "bg-red-100 text-red-700",
};
const STATUS_DOT = {
  "On Duty": "bg-emerald-500", "Scheduled": "bg-blue-500", "Completed": "bg-slate-400",
  "Absent": "bg-amber-500", "Open": "bg-red-500",
};
const SHIFT_STYLE = {
  "Day Shift": { dot: "bg-amber-400", bar: "border-l-amber-400", chip: "bg-amber-50 text-amber-700", head: "text-amber-600" },
  "Evening Shift": { dot: "bg-indigo-400", bar: "border-l-indigo-400", chip: "bg-indigo-50 text-indigo-700", head: "text-indigo-600" },
  "Night Shift": { dot: "bg-slate-700", bar: "border-l-slate-700", chip: "bg-slate-100 text-slate-700", head: "text-slate-600" },
};
const shiftStyle = (n) => SHIFT_STYLE[n] || { dot: "bg-blue-400", bar: "border-l-blue-400", chip: "bg-blue-50 text-blue-700", head: "text-blue-600" };
const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ABSENCE_TYPES = ["Vacation", "Holiday", "Call-In", "Sick", "Training", "Personal Leave", "Other"];
const ABS_META = {
  "Vacation": { code: "V", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  "Holiday": { code: "H", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  "Call-In": { code: "C", cls: "bg-red-100 text-red-700 border-red-200" },
  "Sick": { code: "S", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  "Training": { code: "T", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  "Personal Leave": { code: "P", cls: "bg-purple-100 text-purple-700 border-purple-200" },
  "Other": { code: "O", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const absCode = (t) => (ABS_META[t] || ABS_META.Other).code;

// ---- Monthly-print helpers (compact abbreviations; used ONLY in print) ----
function _tc(s) { return s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : ""; }

// Unique per-employee abbreviations: first initial + growing last-name slice.
// Zachary Chance→ZCh, Zoe Chase→ZCha, Zack Chandler→ZChan. Falls back to first-name
// letters, then a numeric suffix, guaranteeing uniqueness within the printed schedule.
function buildAbbrevMap(employees) {
  const used = new Map();
  const map = {};
  const sorted = [...employees].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  for (const e of sorted) {
    const parts = (e.name || "").trim().split(/\s+/).filter(Boolean);
    const first = parts[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1] : "";
    const fi = (first[0] || "?").toUpperCase();
    let abbr = null;
    if (last) {
      for (let k = 2; k <= last.length; k++) {
        const cand = fi + _tc(last.slice(0, k));
        if (!used.has(cand)) { abbr = cand; break; }
      }
    }
    if (!abbr) { // extend first name (fallback for identical/short last names)
      const lastFull = last ? _tc(last) : "";
      for (let m = 2; m <= Math.max(first.length, 2); m++) {
        const cand = _tc(first.slice(0, m)) + lastFull;
        if (!used.has(cand)) { abbr = cand; break; }
      }
    }
    if (!abbr) { // last resort: numeric suffix
      const base = fi + _tc(last.slice(0, 2)) || fi;
      let n = 2; while (used.has(base + n)) n++; abbr = base + n;
    }
    used.set(abbr, e.id);
    map[e.id] = abbr;
  }
  return map;
}

// "07:00 AM" -> "7a", "03:30 PM" -> "3:30p"
function _compact(disp) {
  if (!disp) return "";
  const [hm, ap] = disp.split(" ");
  const [h, m] = (hm || "").split(":");
  const suffix = (ap || "").toUpperCase() === "AM" ? "a" : "p";
  const hh = String(parseInt(h, 10));
  return (m && m !== "00") ? `${hh}:${m}${suffix}` : `${hh}${suffix}`;
}
function compactRange(a, tz) {
  return `${_compact(fmtTime(a.start, tz))}–${_compact(fmtTime(a.end, tz))}`;
}
const _SHIFT_ABBR = { day: "Day", evening: "Eve", night: "Night", midnight: "Mid" };
function shortShift(name) {
  const base = (name || "").replace(/\s*shift\s*$/i, "").trim();
  return _SHIFT_ABBR[base.toLowerCase()] || base || "Shift";
}


function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function parseKey(key) { const [y, m, d] = key.split("-").map(Number); return new Date(y, m - 1, d); }
function keyLabel(key, opts) {
  if (!key) return "—";
  return parseKey(key).toLocaleDateString(undefined, opts || { weekday: "short", month: "short", day: "numeric" });
}
function isMobile() {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches;
}

const EMPTY = { division: "", employee_id: "", employee_name: "", rank_role: "", shift_name: "", start: "", end: "", supervisor_id: "", supervisor_name: "", team_unit: "", status: "Scheduled", note: "" };

export default function Schedule() {
  const { user } = useAuth();
  const location = useLocation();
  const [personalView, setPersonalView] = useState(user?.role === "employee" ? "mine" : "full");
  const viewInitedRef = useRef(false);
  useEffect(() => {
    if (!viewInitedRef.current && user) {
      viewInitedRef.current = true;
      if (user.role === "employee") setPersonalView("mine");
    }
  }, [user]);
  useEffect(() => {
    if (new URLSearchParams(location.search).get("me") === "1") setPersonalView("mine");
  }, [location.search]);
  const canManage = canManageRecords(user);
  const tz = user?.org_timezone;
  const [view, setView] = useState("monthly");
  const [anchor, setAnchor] = useState(new Date());
  const [selectedKey, setSelectedKey] = useState(todayKey(user?.org_timezone));
  const [assignments, setAssignments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [members, setMembers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [showActivity, setShowActivity] = useState(false);
  const [filters, setFilters] = useState({ division: "all", shift: "all", role: "all", status: "all", employee: "" });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  // Where the user came from when opening a day's detail (for "Back to Calendar")
  const [origin, setOrigin] = useState(null);

  // Bulk weekly scheduling + copy previous week
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);

  // Absences
  const [absences, setAbsences] = useState([]);
  const [showAbsences, setShowAbsences] = useState(true);
  const [absTypeFilter, setAbsTypeFilter] = useState("all");
  const [absOpen, setAbsOpen] = useState(false);
  const [absEditing, setAbsEditing] = useState(null);
  const [absForm, setAbsForm] = useState(null);
  const [absBusy, setAbsBusy] = useState(false);
  const [scheduleRoles, setScheduleRoles] = useState([]);

  const load = useCallback(() => {
    api.get("/assignments").then((r) => setAssignments(r.data)).catch(() => {});
    api.get("/absences").then((r) => setAbsences(r.data)).catch(() => {});
    api.get("/schedule-roles").then((r) => setScheduleRoles(r.data)).catch(() => {});
    api.get("/divisions").then((r) => setDivisions(r.data)).catch(() => {});
    api.get("/shifts").then((r) => setShifts(r.data)).catch(() => {});
    if (isAdminRole(user)) api.get("/team").then((r) => setMembers(r.data)).catch(() => {});
    else if (user?.role === "supervisor") api.get("/division-team").then((r) => setMembers(r.data.members || [])).catch(() => {});
    if (canManage) api.get("/schedule/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, [user, canManage]);
  useEffect(() => { load(); }, [load]);

  const myDivisionNames = accessibleDivisionNames(user, divisions.filter((d) => d.active !== false));
  const manageableDivisions = isAdminRole(user) ? myDivisionNames : (user?.supervised_divisions || []);
  const canManageDivision = (div) => canManage && manageableDivisions.includes(div);

  // ---- Absences ----
  const absencesForDate = (dateKey) => absences.filter(
    (a) => a.start_date <= dateKey && dateKey <= a.end_date
      && (absTypeFilter === "all" || a.type === absTypeFilter)
  );
  const absenceForEmpDate = (a, dateKey) => absences.find(
    (x) => x.start_date <= dateKey && dateKey <= x.end_date
      && ((a.employee_id && x.employee_id === a.employee_id) || (x.employee_name === a.employee_name))
  );
  const openAbsence = (prefill = {}) => {
    setAbsEditing(null);
    setAbsForm({
      employee_id: prefill.employee_id || "", employee_name: prefill.employee_name || "",
      division: prefill.division || manageableDivisions[0] || "",
      type: prefill.type || "Vacation",
      start_date: prefill.start_date || ymd(new Date()), end_date: prefill.end_date || prefill.start_date || ymd(new Date()),
      notes: "",
    });
    setAbsOpen(true);
  };
  const editAbsence = (ab) => { setAbsEditing(ab); setAbsForm({ ...ab }); setAbsOpen(true); };
  const setAbsEmployee = (id) => {
    const m = members.find((x) => x.id === id);
    setAbsForm((f) => ({ ...f, employee_id: id, employee_name: m?.name || "",
      division: m?.primary_division || f.division }));
  };
  const submitAbsence = async () => {
    if (!absForm?.employee_name) { toast.error("Select an employee"); return; }
    if (!canManageDivision(absForm.division)) { toast.error("Pick a division you manage"); return; }
    if (absForm.end_date < absForm.start_date) { toast.error("End date is before start date"); return; }
    setAbsBusy(true);
    try {
      if (absEditing) await api.put(`/absences/${absEditing.id}`, absForm);
      else await api.post("/absences", absForm);
      toast.success(absEditing ? "Absence updated" : "Absence added");
      setAbsOpen(false); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setAbsBusy(false); }
  };
  const removeAbsence = async () => {
    try { await api.delete(`/absences/${absEditing.id}`); toast.success("Absence removed"); setAbsOpen(false); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  // Compact absence chips for a given day (respects the Show Absences toggle + type filter)
  const AbsenceChips = ({ dateKey }) => {
    if (!showAbsences) return null;
    const items = absencesForDate(dateKey);
    if (items.length === 0) return null;
    return (
      <div className="mt-1 flex flex-wrap gap-1" data-testid={`absences-${dateKey}`}>
        {items.map((ab) => {
          const meta = ABS_META[ab.type] || ABS_META.Other;
          return (
            <button
              key={ab.id}
              type="button"
              data-testid={`absence-chip-${ab.id}`}
              onClick={(e) => { e.stopPropagation(); if (canManageDivision(ab.division)) editAbsence(ab); }}
              className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${meta.cls}`}
              title={`${ab.employee_name} — ${ab.type}${ab.notes ? " · " + ab.notes : ""}`}
            >
              {ab.employee_name.split(" ").map((w) => w[0]).slice(0, 2).join("")} · {ab.type.toUpperCase()}
            </button>
          );
        })}
      </div>
    );
  };
  const shiftOrder = shifts.map((s) => s.name);
  const tKey = todayKey(tz);

  // Filtered by the control bar (not by date; date is handled per-view)
  const filtered = useMemo(() => assignments.filter((a) => {
    if (filters.division !== "all" && a.division !== filters.division) return false;
    if (filters.shift !== "all" && a.shift_name !== filters.shift) return false;
    if (filters.role !== "all" && (a.rank_role || "") !== filters.role) return false;
    if (filters.status !== "all" && a.status !== filters.status) return false;
    if (filters.employee && !(a.employee_name || "").toLowerCase().includes(filters.employee.toLowerCase())) return false;
    return true;
  }), [assignments, filters]);

  const byDate = useMemo(() => {
    const map = {};
    filtered.forEach((a) => {
      const k = zonedDateKey(a.start, tz);
      (map[k] = map[k] || []).push(a);
    });
    Object.values(map).forEach((list) => list.sort((x, y) => (x.start || "").localeCompare(y.start || "")));
    return map;
  }, [filtered, tz]);

  const roles = Array.from(new Set(assignments.map((a) => a.rank_role).filter(Boolean)));

  // Grids
  const monthCells = useMemo(() => {
    const yr = anchor.getFullYear(); const mo = anchor.getMonth();
    const first = new Date(yr, mo, 1);
    const gridStart = new Date(yr, mo, 1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart); d.setDate(gridStart.getDate() + i);
      return { key: ymd(d), inMonth: d.getMonth() === mo, day: d.getDate() };
    });
  }, [anchor]);
  const weekKeys = useMemo(() => {
    const start = new Date(anchor); start.setDate(anchor.getDate() - anchor.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return ymd(d); });
  }, [anchor]);
  const dayKey = ymd(anchor);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, division: manageableDivisions[0] || "", shift_name: shifts[0]?.name || "" });
    setDialogOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      division: a.division, employee_id: a.employee_id || "", employee_name: a.employee_name || "",
      rank_role: a.rank_role || "", shift_name: a.shift_name, start: utcIsoToZonedWall(a.start, tz), end: utcIsoToZonedWall(a.end, tz),
      supervisor_id: a.supervisor_id || "", supervisor_name: a.supervisor_name || "", team_unit: a.team_unit || "",
      status: a.status, note: a.note || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    const payload = {
      ...form,
      start: form.start ? zonedWallToUtcIso(form.start, tz) : "",
      end: form.end ? zonedWallToUtcIso(form.end, tz) : "",
    };
    try {
      if (editing) await api.put(`/assignments/${editing.id}`, payload);
      else await api.post("/assignments", payload);
      toast.success(editing ? "Assignment updated" : "Assignment created");
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (a) => {
    try { await api.delete(`/assignments/${a.id}`); toast.success("Assignment removed"); setDetail(null); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const setEmployee = (id) => {
    const m = members.find((x) => x.id === id);
    setForm({ ...form, employee_id: id === "open" ? "" : id, employee_name: id === "open" ? "" : (m?.name || ""), status: id === "open" ? "Open" : form.status });
  };

  // ---------- Bulk weekly scheduling ----------
  const prevWeekKeys = useMemo(() => {
    const start = new Date(anchor); start.setDate(anchor.getDate() - anchor.getDay() - 7);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return ymd(d); });
  }, [anchor]);

  const addHoursToTime = (hhmm, hrs) => {
    const [h, m] = (hhmm || "08:00").split(":").map(Number);
    const t = ((h + hrs) % 24 + 24) % 24;
    return `${String(t).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
  };
  const shiftStart = (name) => {
    const s = shifts.find((x) => x.name === name);
    return (s && s.start_time) || "08:00";
  };

  const monthDayKeys = useMemo(() => monthCells.filter((c) => c.inMonth).map((c) => c.key), [monthCells]);

  // Unique compact abbreviations for everyone appearing in the currently loaded schedule (print only).
  const abbrevMap = useMemo(() => {
    const seen = new Map();
    assignments.forEach((a) => {
      const id = a.employee_id || a.employee_name;
      if (id && a.employee_name && !seen.has(id)) seen.set(id, { id, name: a.employee_name });
    });
    return buildAbbrevMap([...seen.values()]);
  }, [assignments]);
  const abbrevOf = (a) => {
    if (!a.employee_id && !a.employee_name) return "Open";
    return abbrevMap[a.employee_id || a.employee_name] || (a.employee_name || "Open");
  };
  // role name -> short code (print). Only used when the org has schedule roles.
  const roleCodeMap = useMemo(() => {
    const m = {};
    scheduleRoles.forEach((r) => { if (r.name) m[r.name] = r.short_code || ""; });
    return m;
  }, [scheduleRoles]);
  const roleCodeOf = (a) => {
    const nm = (a.schedule_role || "").trim();
    if (!nm) return "";
    return roleCodeMap[nm] || nm.slice(0, 3).toUpperCase();
  };
  // Shift groups for print: group by CONFIGURED SHIFT NAME (not exact hours). Employees on the
  // group's standard/most-common hours are listed compactly under a timed heading; anyone with
  // different hours keeps their own time beside their name. Different configured shifts stay separate.
  const printGroups = (items) => {
    const byShift = new Map();
    (items || []).forEach((a) => {
      const key = a.shift_name || "—";
      if (!byShift.has(key)) byShift.set(key, { shiftName: key, start: a.start || "", entries: [] });
      const g = byShift.get(key);
      g.entries.push(a);
      if (!g.start || (a.start || "") < g.start) g.start = a.start || g.start;
    });
    const groups = [...byShift.values()];
    groups.forEach((g) => {
      const counts = {};
      g.entries.forEach((a) => { const r = compactRange(a, tz); counts[r] = (counts[r] || 0) + 1; });
      let stdRange = null, maxc = 0;
      Object.entries(counts).forEach(([r, c]) => { if (c > maxc) { maxc = c; stdRange = r; } });
      const allDistinct = g.entries.length > 1 && maxc === 1; // no shared/standard hours
      g.headingRange = allDistinct ? null : stdRange;
      g.standard = allDistinct ? [] : g.entries.filter((a) => compactRange(a, tz) === stdRange);
      g.exceptions = (allDistinct ? g.entries.slice() : g.entries.filter((a) => compactRange(a, tz) !== stdRange))
        .sort((x, y) => (x.start || "").localeCompare(y.start || ""));
      g.label = `${shortShift(g.shiftName).toUpperCase()}${g.headingRange ? " " + g.headingRange : ""}`;
    });
    return groups.sort((x, y) => {
      const rx = shiftOrder.indexOf(x.shiftName), ry = shiftOrder.indexOf(y.shiftName);
      const ax = rx === -1 ? 999 : rx, ay = ry === -1 ? 999 : ry;
      if (ax !== ay) return ax - ay;
      return (x.start || "").localeCompare(y.start || "");
    });
  };
  // Roles actually present in the currently loaded schedule (for a compact print legend).
  const rolesInUse = useMemo(() => {
    const seen = new Set();
    assignments.forEach((a) => { if ((a.schedule_role || "").trim()) seen.add(a.schedule_role.trim()); });
    return scheduleRoles.filter((r) => seen.has(r.name));
  }, [assignments, scheduleRoles]);

  // Responsive monthly-print sizing: fewer week rows + lighter days => larger, more readable text.
  const monthPrintMeta = useMemo(() => {
    const weeks = Math.max(5, Math.round(monthCells.length / 7));
    let maxLines = 0;
    monthCells.forEach((c) => {
      if (!c.inMonth) return;
      const groups = printGroups(byDate[c.key]);
      let lines = groups.reduce((n, g) => n + 1 + (g.standard.length ? 1 : 0) + g.exceptions.length, 0);
      if (showAbsences) {
        const extra = absencesForDate(c.key).filter(
          (ab) => !(byDate[c.key] || []).some((a) => (ab.employee_id && a.employee_id === ab.employee_id) || a.employee_name === ab.employee_name)
        ).length;
        if (extra) lines += 2;
      }
      maxLines = Math.max(maxLines, lines);
    });
    const density = maxLines <= 5 ? "d1" : maxLines <= 8 ? "d2" : "d3";
    return { weeks, density };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthCells, byDate, assignments, absences, showAbsences]);

  const openBulk = () => {
    const div = manageableDivisions[0] || "";
    const sh = shifts[0]?.name || "";
    const st = shiftStart(sh);
    setBulk({
      mode: view === "monthly" ? "month" : "week",
      division: div, employee_id: "", employee_name: "", rank_role: "", shift_name: sh,
      supervisor_name: "", team_unit: "", status: "Scheduled", note: "",
      start_time: st, end_time: addHoursToTime(st, 8),
      days: {}, // dateKey -> true
    });
    setBulkOpen(true);
  };

  const setBulkShift = (name) => {
    const st = shiftStart(name);
    setBulk((b) => ({ ...b, shift_name: name, start_time: st, end_time: addHoursToTime(st, 8) }));
  };
  const setBulkEmployee = (id) => {
    const m = members.find((x) => x.id === id);
    setBulk((b) => ({
      ...b, employee_id: id === "open" ? "" : id,
      employee_name: id === "open" ? "" : (m?.name || ""),
      status: id === "open" ? "Open" : (b.status === "Open" ? "Scheduled" : b.status),
    }));
  };
  const toggleBulkDay = (key) => setBulk((b) => ({ ...b, days: { ...b.days, [key]: !b.days[key] } }));

  // Month-mode weekday shortcuts (wd: 0=Sun..6=Sat). null = all weekdays (Mon-Fri).
  const selectWeekday = (wd) => setBulk((b) => {
    const days = { ...b.days };
    monthDayKeys.forEach((k) => {
      const d = parseKey(k).getDay();
      if (wd === "weekdays" ? (d >= 1 && d <= 5) : d === wd) days[k] = true;
    });
    return { ...b, days };
  });
  const clearBulkDays = () => setBulk((b) => ({ ...b, days: {} }));

  const bulkSelectedKeys = bulk ? Object.keys(bulk.days).filter((k) => bulk.days[k]) : [];
  const bulkDupCount = useMemo(() => {
    if (!bulk) return 0;
    const emp = bulk.employee_id || null;
    return bulkSelectedKeys.filter((dk) =>
      assignments.some((a) => a.division === bulk.division && a.shift_name === bulk.shift_name
        && (a.employee_id || null) === emp && zonedDateKey(a.start, tz) === dk)
    ).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulk, assignments, tz]);
  const bulkNewCount = bulkSelectedKeys.length - bulkDupCount;

  // Copy Previous Month
  const [copyMonthOpen, setCopyMonthOpen] = useState(false);
  const [copyMonthBusy, setCopyMonthBusy] = useState(false);
  const [monthPreview, setMonthPreview] = useState(null);
  const prevMonthAnchor = useMemo(() => new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1), [anchor]);
  const monthName = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const openCopyMonth = async () => {
    setMonthPreview(null);
    setCopyMonthOpen(true);
    const prevStart = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
    const curStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    try {
      const { data } = await api.post("/schedule/copy-month", {
        source_start: zonedWallToUtcIso(`${ymd(prevStart)}T00:00`, tz),
        source_end: zonedWallToUtcIso(`${ymd(curStart)}T00:00`, tz),
        dest_year: anchor.getFullYear(), dest_month: anchor.getMonth() + 1,
        preview: true, mode: "skip",
      });
      setMonthPreview(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setMonthPreview({ error: true }); }
  };

  const submitCopyMonth = async () => {
    setCopyMonthBusy(true);
    const prevStart = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
    const curStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    try {
      const { data } = await api.post("/schedule/copy-month", {
        source_start: zonedWallToUtcIso(`${ymd(prevStart)}T00:00`, tz),
        source_end: zonedWallToUtcIso(`${ymd(curStart)}T00:00`, tz),
        dest_year: anchor.getFullYear(), dest_month: anchor.getMonth() + 1,
        preview: false, mode: "skip",
      });
      const skipped = data.skipped ? `, skipped ${data.skipped} conflict${data.skipped > 1 ? "s" : ""}` : "";
      toast.success(`Copied ${data.created_count} shift${data.created_count === 1 ? "" : "s"}${skipped}`);
      setCopyMonthOpen(false);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setCopyMonthBusy(false); }
  };

  const submitBulk = async () => {
    if (!bulk?.division || !canManageDivision(bulk.division)) { toast.error("Pick a division you manage"); return; }
    if (!bulk.shift_name) { toast.error("Pick a shift"); return; }
    const selectedDays = Object.keys(bulk.days).filter((k) => bulk.days[k]);
    if (selectedDays.length === 0) { toast.error("Select at least one day"); return; }
    const entries = selectedDays.map((dk) => {
      const start = zonedWallToUtcIso(`${dk}T${bulk.start_time}`, tz);
      // If end time is earlier/equal, treat as overnight (next day)
      let endKey = dk;
      if (bulk.end_time <= bulk.start_time) {
        const d = parseKey(dk); d.setDate(d.getDate() + 1); endKey = ymd(d);
      }
      const end = zonedWallToUtcIso(`${endKey}T${bulk.end_time}`, tz);
      return { start, end };
    });
    setBulkBusy(true);
    try {
      const { data } = await api.post("/assignments/bulk", {
        division: bulk.division, employee_id: bulk.employee_id || null, employee_name: bulk.employee_name,
        rank_role: bulk.rank_role, shift_name: bulk.shift_name, supervisor_name: bulk.supervisor_name,
        team_unit: bulk.team_unit, status: bulk.status, note: bulk.note, entries,
      });
      const skipped = data.skipped ? `, skipped ${data.skipped} duplicate${data.skipped > 1 ? "s" : ""}` : "";
      toast.success(`Added ${data.created_count} shift${data.created_count === 1 ? "" : "s"}${skipped}`);
      setBulkOpen(false);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  // ---------- Copy previous week ----------
  const destWeekCount = useMemo(
    () => weekKeys.reduce((n, k) => n + ((byDate[k] || []).filter((a) => canManageDivision(a.division)).length), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekKeys, byDate]
  );
  const prevWeekCount = useMemo(
    () => prevWeekKeys.reduce((n, k) => n + ((byDate[k] || []).filter((a) => canManageDivision(a.division)).length), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prevWeekKeys, byDate]
  );

  const submitCopyWeek = async () => {
    setCopyBusy(true);
    try {
      const source_start = zonedWallToUtcIso(`${prevWeekKeys[0]}T00:00`, tz);
      const source_end = zonedWallToUtcIso(`${weekKeys[0]}T00:00`, tz);
      const { data } = await api.post("/schedule/copy-week", { source_start, source_end, delta_days: 7, mode: "skip" });
      const skipped = data.skipped ? `, skipped ${data.skipped} conflict${data.skipped > 1 ? "s" : ""}` : "";
      toast.success(`Copied ${data.created_count} shift${data.created_count === 1 ? "" : "s"}${skipped}`);
      setCopyOpen(false);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setCopyBusy(false); }
  };

  const rangeLabel = view === "daily"
    ? keyLabel(dayKey, { weekday: "long", month: "long", day: "numeric" })
    : view === "weekly"
      ? `${keyLabel(weekKeys[0], { month: "long", day: "numeric" })} – ${keyLabel(weekKeys[6], { month: "long", day: "numeric" })}`
      : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const step = (dir) => {
    const d = new Date(anchor);
    if (view === "daily") d.setDate(d.getDate() + dir);
    else if (view === "weekly") d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    setAnchor(d);
  };
  const goToday = () => { setOrigin(null); setAnchor(new Date()); setSelectedKey(tKey); };

  const enterDay = (key) => {
    // Remember the calendar we came from so we can return to the exact state.
    setOrigin({ view, anchor: new Date(anchor), scrollY: typeof window !== "undefined" ? window.scrollY : 0 });
    setSelectedKey(key);
    setAnchor(parseKey(key));
    setView("daily");
  };

  const backToCalendar = () => {
    const o = origin;
    if (o) {
      setView(o.view);
      setAnchor(new Date(o.anchor));
      setOrigin(null);
      if (typeof window !== "undefined") {
        setTimeout(() => window.scrollTo({ top: o.scrollY || 0, behavior: "auto" }), 0);
      }
    } else {
      setView("monthly");
    }
  };

  const onDateClick = (key) => {
    setSelectedKey(key);
    if (!isMobile()) enterDay(key);
  };

  // ---------- Compact item (month / agenda) ----------
  const CompactItem = ({ a }) => {
    const st = shiftStyle(a.shift_name);
    return (
      <button
        data-testid={`assignment-${a.id}`}
        onClick={(e) => { e.stopPropagation(); setDetail(a); }}
        className={`flex w-full items-center gap-1 truncate rounded border-l-2 ${st.bar} bg-slate-50 px-1.5 py-0.5 text-left text-[11px] hover:bg-slate-100`}
      >
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT[a.status] || "bg-slate-400"}`} />
        <span className="truncate font-medium text-slate-700">{a.employee_name || "Open"}</span>
        <span className="ml-auto shrink-0 text-slate-400">{fmtTime(a.start, tz)}</span>
      </button>
    );
  };

  // ---------- Weekly compact row ----------
  const WeekRow = ({ a }) => (
    <button
      data-testid={`assignment-${a.id}`}
      onClick={() => setDetail(a)}
      className="flex w-full items-center justify-between gap-2 rounded-md border border-slate-100 px-2 py-1.5 text-left text-xs hover:bg-slate-50"
    >
      <div className="min-w-0">
        <p className="truncate font-medium text-slate-800">{a.employee_name || "Open position"}</p>
        <p className="truncate text-[11px] text-slate-400">{a.rank_role || "—"} · {fmtTime(a.start, tz)}–{fmtTime(a.end, tz)}</p>
      </div>
      <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_COLOR[a.status] || "bg-slate-100 text-slate-600"}`}>{a.status}</span>
    </button>
  );

  // ---------- Daily detailed card ----------
  const DayCard = ({ a }) => (
    <Card
      data-testid={`assignment-${a.id}`}
      onClick={() => setDetail(a)}
      className="cursor-pointer rounded-md border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-blue-300"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="font-semibold text-slate-900">{a.employee_name || "Open position"}</p>
          <p className="text-xs text-slate-500">{a.division} · {a.shift_name}{a.rank_role ? ` · ${a.rank_role}` : ""}</p>
        </div>
        <Badge className={STATUS_COLOR[a.status] || "bg-slate-100 text-slate-600"}>{a.status}</Badge>
      </div>
      <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
        <Clock className="h-3.5 w-3.5" /> {fmtTime(a.start, tz)} – {fmtTime(a.end, tz)}
        {a.team_unit && <span className="ml-2 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.team_unit}</span>}
      </div>
      {a.supervisor_name && <p className="mt-1 text-xs text-slate-400">Supervisor: {a.supervisor_name}</p>}
      {a.note && <p className="mt-1 text-xs italic text-slate-500">{a.note}</p>}
      {a.status === "Open" && <p className="mt-1 text-xs font-semibold text-red-600">Open coverage — needs assignment</p>}
    </Card>
  );

  const shiftGroups = (list) => {
    const groups = {};
    (list || []).forEach((a) => { (groups[a.shift_name] = groups[a.shift_name] || []).push(a); });
    const names = [...shiftOrder.filter((n) => groups[n]), ...Object.keys(groups).filter((n) => !shiftOrder.includes(n))];
    return names.map((n) => [n, groups[n]]);
  };

  const ScheduleModeToggle = () => (
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="schedule-mode-toggle">
      <button data-testid="mode-full" onClick={() => setPersonalView("full")}
        className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${personalView === "full" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
        Full Schedule
      </button>
      <button data-testid="mode-mine" onClick={() => setPersonalView("mine")}
        className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${personalView === "mine" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
        My Schedule
      </button>
    </div>
  );

  if (personalView === "mine") {
    return (
      <div className="animate-fade-up space-y-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Operations</p>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">My Schedule</h1>
          <p className="mt-1 text-sm text-slate-500">Only the shifts and absences assigned to you.</p>
        </div>
        <ScheduleModeToggle />
        <MySchedule />
      </div>
    );
  }

  return (
    <div className="animate-fade-up space-y-6">
      <ScheduleModeToggle />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Operations</p>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Shift Roster</h1>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-400" data-testid="schedule-tz-banner">
            <Clock className="h-3 w-3" /> {tzBanner(tz)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && view !== "daily" && (
            <Button
              data-testid={view === "monthly" ? "copy-month-button" : "copy-week-button"}
              variant="outline" className="gap-2"
              onClick={() => (view === "monthly" ? openCopyMonth() : setCopyOpen(true))}
            >
              <Copy className="h-4 w-4" /> {view === "monthly" ? "Copy Previous Month" : "Copy Previous Week"}
            </Button>
          )}
          {canManage && view !== "daily" && (
            <Button data-testid="bulk-add-button" variant="outline" className="gap-2 text-blue-700 border-blue-200 hover:bg-blue-50" onClick={openBulk}>
              <CalendarPlus className="h-4 w-4" /> Bulk Add
            </Button>
          )}
          {canManage && (
            <Button data-testid="schedule-activity-toggle" variant="outline" className="gap-2" onClick={() => setShowActivity((s) => !s)}>
              <History className="h-4 w-4" /> Activity
            </Button>
          )}
          <Button
            data-testid="show-absences-toggle"
            variant={showAbsences ? "default" : "outline"}
            className={`gap-2 ${showAbsences ? "bg-slate-800 hover:bg-slate-900 text-white" : ""}`}
            onClick={() => setShowAbsences((s) => !s)}
          >
            <CalendarOff className="h-4 w-4" /> {showAbsences ? "Absences: On" : "Absences: Off"}
          </Button>
          {canManage && (
            <Button data-testid="add-absence-button" variant="outline" className="gap-2 text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => openAbsence()}>
              <CalendarOff className="h-4 w-4" /> Add Absence
            </Button>
          )}
          <Button data-testid="print-schedule-button" variant="outline" className="gap-2" onClick={() => window.print()}>
            <Printer className="h-4 w-4" /> Print Schedule
          </Button>
          {canManage && (
            <Button data-testid="new-assignment-button" onClick={openCreate} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="h-4 w-4" /> New Assignment
            </Button>
          )}
        </div>
      </div>

      {/* View + navigator + filters */}
      <Card className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex rounded-md border border-slate-200 p-0.5" data-testid="schedule-view-toggle">
            {["daily", "weekly", "monthly"].map((v) => (
              <button key={v} data-testid={`view-${v}`} onClick={() => { setOrigin(null); setView(v); }}
                className={`rounded px-3 py-1.5 text-sm font-medium capitalize transition-colors ${view === v ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
                {v}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToday} data-testid="schedule-today" className="gap-1"><CalendarCheck className="h-4 w-4" /> Today</Button>
            <Button variant="outline" size="sm" onClick={() => step(-1)} data-testid="schedule-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="min-w-44 text-center text-sm font-semibold text-slate-800" data-testid="schedule-range-label">{rangeLabel}</span>
            <Button variant="outline" size="sm" onClick={() => step(1)} data-testid="schedule-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Select value={filters.division} onValueChange={(v) => setFilters({ ...filters, division: v })}>
            <SelectTrigger data-testid="filter-division" className="h-9 w-36 text-xs"><SelectValue placeholder="Division" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All divisions</SelectItem>{myDivisionNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.shift} onValueChange={(v) => setFilters({ ...filters, shift: v })}>
            <SelectTrigger data-testid="filter-shift" className="h-9 w-36 text-xs"><SelectValue placeholder="Shift" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All shifts</SelectItem>{shifts.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.role} onValueChange={(v) => setFilters({ ...filters, role: v })}>
            <SelectTrigger data-testid="filter-role" className="h-9 w-32 text-xs"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All roles</SelectItem>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status" className="h-9 w-36 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="filter-employee" value={filters.employee} onChange={(e) => setFilters({ ...filters, employee: e.target.value })} placeholder="Employee" className="h-9 w-40 text-xs" />
          <Select value={absTypeFilter} onValueChange={setAbsTypeFilter}>
            <SelectTrigger data-testid="filter-absence-type" className="h-9 w-40 text-xs"><SelectValue placeholder="Absence type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All absence types</SelectItem>{ABSENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Legend */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 pt-3 text-[11px] text-slate-500" data-testid="schedule-legend">
          <span className="font-semibold uppercase tracking-wider text-slate-400">Shifts</span>
          {Object.keys(SHIFT_STYLE).map((n) => (
            <span key={n} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${shiftStyle(n).dot}`} /> {n}</span>
          ))}
          <span className="ml-2 font-semibold uppercase tracking-wider text-slate-400">Status</span>
          {STATUSES.map((s) => (
            <span key={s} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${STATUS_DOT[s]}`} /> {s}</span>
          ))}
        </div>
      </Card>

      {showActivity && canManage && (
        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="schedule-activity">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><History className="h-4 w-4 text-slate-500" /> Schedule Activity</h2>
          <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
            {activity.length === 0 ? <p className="text-sm text-slate-400">No schedule activity yet.</p> : activity.map((e, i) => (
              <div key={i} className="rounded-md border border-slate-100 px-3 py-2 text-sm">
                <span className="font-semibold text-slate-800">{e.by_name}</span> {e.action} {e.employee_name} · {e.division} {e.shift_name}
                <span className="ml-2 text-xs text-slate-400">{new Date(e.at).toLocaleString("en-US", { timeZone: tz || "America/Chicago", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ============ DAILY ============ */}
      {view === "daily" && (
        <div className="space-y-3" data-testid="schedule-daily">
          {origin && (
            <button
              data-testid="back-to-calendar"
              onClick={backToCalendar}
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Calendar</span>
              <span className="sm:hidden">Calendar</span>
            </button>
          )}
          {(byDate[dayKey] || []).length === 0 ? (
            <Card className="rounded-md border border-slate-200 bg-white p-10 text-center text-slate-400 shadow-sm">No assignments on {keyLabel(dayKey)}.</Card>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {(byDate[dayKey] || []).map((a) => <DayCard key={a.id} a={a} />)}
            </div>
          )}
          <AbsenceChips dateKey={dayKey} />
        </div>
      )}

      {/* ============ WEEKLY ============ */}
      {view === "weekly" && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-7" data-testid="schedule-weekly">
          {weekKeys.map((key) => {
            const groups = shiftGroups(byDate[key]);
            return (
              <Card key={key} data-testid={`week-day-${key}`} className={`rounded-md border bg-white p-3 shadow-sm ${key === tKey ? "border-blue-400 ring-1 ring-blue-200" : "border-slate-200"}`}>
                <button onClick={() => onDateClick(key)} className="mb-2 flex w-full items-baseline justify-between text-left">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{keyLabel(key, { weekday: "short" })}</span>
                  <span className={`text-lg font-black ${key === tKey ? "text-blue-600" : "text-slate-900"}`}>{parseKey(key).getDate()}</span>
                </button>
                {groups.length === 0 ? (
                  <p className="py-4 text-center text-xs text-slate-300">—</p>
                ) : groups.map(([shift, list]) => (
                  <div key={shift} className="mb-2">
                    <p className={`mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${shiftStyle(shift).head}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${shiftStyle(shift).dot}`} /> {shift}
                    </p>
                    <div className="space-y-1">{list.map((a) => <WeekRow key={a.id} a={a} />)}</div>
                  </div>
                ))}
                <AbsenceChips dateKey={key} />
              </Card>
            );
          })}
        </div>
      )}

      {/* ============ MONTHLY ============ */}
      {view === "monthly" && (
        <>
          {/* Desktop / tablet calendar grid */}
          <Card className="hidden overflow-x-auto rounded-md border border-slate-200 bg-white p-0 shadow-sm sm:block" data-testid="schedule-monthly">
            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
              {WEEKDAYS.map((d) => <div key={d} className="px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wider text-slate-500">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {monthCells.map((cell) => {
                const items = byDate[cell.key] || [];
                const shown = items.slice(0, 3);
                const extra = items.length - shown.length;
                return (
                  <div
                    key={cell.key}
                    data-testid={`calendar-day-${cell.key}`}
                    onClick={() => onDateClick(cell.key)}
                    className={`min-h-[104px] cursor-pointer border-b border-r border-slate-100 p-1.5 transition-colors hover:bg-slate-50 ${cell.inMonth ? "" : "bg-slate-50/50"}`}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${cell.key === tKey ? "bg-blue-600 text-white" : cell.inMonth ? "text-slate-700" : "text-slate-300"}`}>{cell.day}</span>
                    </div>
                    <div className="space-y-0.5">
                      {shown.map((a) => <CompactItem key={a.id} a={a} />)}
                      {extra > 0 && (
                        <button
                          data-testid={`calendar-more-${cell.key}`}
                          onClick={(e) => { e.stopPropagation(); enterDay(cell.key); }}
                          className="w-full rounded px-1.5 text-left text-[11px] font-semibold text-blue-600 hover:underline"
                        >+{extra} more</button>
                      )}
                      <AbsenceChips dateKey={cell.key} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Mobile agenda-style month */}
          <div className="space-y-3 sm:hidden" data-testid="schedule-monthly-mobile">
            <Card className="rounded-md border border-slate-200 bg-white p-2 shadow-sm">
              <div className="grid grid-cols-7 gap-0.5">
                {WEEKDAYS.map((d) => <div key={d} className="py-1 text-center text-[9px] font-bold uppercase text-slate-400">{d[0]}</div>)}
                {monthCells.map((cell) => {
                  const count = (byDate[cell.key] || []).length;
                  const sel = cell.key === selectedKey;
                  return (
                    <button
                      key={cell.key}
                      data-testid={`calendar-mini-${cell.key}`}
                      onClick={() => setSelectedKey(cell.key)}
                      className={`flex aspect-square flex-col items-center justify-center rounded text-xs ${sel ? "bg-blue-600 text-white" : cell.key === tKey ? "ring-1 ring-blue-300 text-slate-800" : cell.inMonth ? "text-slate-700" : "text-slate-300"}`}
                    >
                      {cell.day}
                      {count > 0 && <span className={`mt-0.5 h-1 w-1 rounded-full ${sel ? "bg-white" : "bg-blue-500"}`} />}
                    </button>
                  );
                })}
              </div>
            </Card>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">{keyLabel(selectedKey, { weekday: "long", month: "long", day: "numeric" })}</p>
              <div className="space-y-2" data-testid="mobile-agenda-list">
                {(byDate[selectedKey] || []).length === 0
                  ? <Card className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-slate-400 shadow-sm">No assignments.</Card>
                  : (byDate[selectedKey] || []).map((a) => <WeekRowCard key={a.id} a={a} onClick={() => setDetail(a)} tz={tz} />)}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ============ ASSIGNMENT DETAILS MODAL ============ */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent data-testid="assignment-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <button
                  data-testid="modal-back-to-calendar"
                  onClick={() => setDetail(null)}
                  className="mb-1 inline-flex w-fit items-center gap-1 text-xs font-medium text-slate-400 transition-colors hover:text-slate-700"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Back to Calendar</span>
                  <span className="sm:hidden">Calendar</span>
                </button>
                <DialogTitle className="flex items-center gap-2">
                  {detail.employee_name || "Open position"}
                  <Badge className={STATUS_COLOR[detail.status] || "bg-slate-100 text-slate-600"}>{detail.status}</Badge>
                </DialogTitle>
                <DialogDescription>{keyLabel(zonedDateKey(detail.start, tz), { weekday: "long", month: "long", day: "numeric" })}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2 text-slate-700"><span className={`h-2 w-2 rounded-full ${shiftStyle(detail.shift_name).dot}`} /> {detail.division} · {detail.shift_name}{detail.rank_role ? ` · ${detail.rank_role}` : ""}</p>
                <p className="flex items-center gap-2 text-slate-600"><Clock className="h-4 w-4 text-slate-400" /> {fmtTime(detail.start, tz)} – {fmtTime(detail.end, tz)}</p>
                {detail.team_unit && <p className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-slate-400" /> {detail.team_unit}</p>}
                {detail.supervisor_name && <p className="flex items-center gap-2 text-slate-600"><ShieldCheck className="h-4 w-4 text-slate-400" /> Supervisor: {detail.supervisor_name}</p>}
                {detail.status === "Open" && <p className="font-semibold text-red-600">Open coverage — needs assignment</p>}
                {detail.note && <p className="rounded-md bg-slate-50 p-2 text-slate-600 italic">{detail.note}</p>}
              </div>
              {canManageDivision(detail.division) && (
                <DialogFooter className="mt-2">
                  <Button data-testid={`edit-assignment-${detail.id}`} variant="outline" className="gap-1" onClick={() => { const a = detail; setDetail(null); openEdit(a); }}>
                    <Pencil className="h-4 w-4" /> Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`delete-assignment-${detail.id}`} variant="outline" className="gap-1 text-red-600 border-red-200 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" /> Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove this assignment?</AlertDialogTitle>
                        <AlertDialogDescription>This removes the roster assignment. This action cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`confirm-delete-${detail.id}`} onClick={() => remove(detail)} className="bg-red-600 hover:bg-red-700 text-white">Remove</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ CREATE / EDIT DIALOG ============ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Assignment" : "New Assignment"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>Division</Label>
                <Select value={form.division} onValueChange={(v) => setForm({ ...form, division: v })}>
                  <SelectTrigger data-testid="assign-division" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{manageableDivisions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Shift</Label>
                <Select value={form.shift_name} onValueChange={(v) => setForm({ ...form, shift_name: v })}>
                  <SelectTrigger data-testid="assign-shift" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{shifts.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className={labelCls}>Employee (or Open)</Label>
              <Select value={form.employee_id || "open"} onValueChange={setEmployee}>
                <SelectTrigger data-testid="assign-employee" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open position (unassigned)</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>Rank / Role</Label>
                <Input data-testid="assign-rank" value={form.rank_role} onChange={(e) => setForm({ ...form, rank_role: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className={labelCls}>Team / Unit</Label>
                <Input data-testid="assign-team" value={form.team_unit} onChange={(e) => setForm({ ...form, team_unit: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>Start</Label>
                <Input data-testid="assign-start" type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className={labelCls}>End</Label>
                <Input data-testid="assign-end" type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <p className="-mt-1 text-xs text-slate-400" data-testid="assign-tz-note">{tzBanner(tz)}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>Assigned supervisor</Label>
                <Input data-testid="assign-supervisor" value={form.supervisor_name} onChange={(e) => setForm({ ...form, supervisor_name: e.target.value })} className="mt-1.5" placeholder="Name" />
              </div>
              <div>
                <Label className={labelCls}>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="assign-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className={labelCls}>Note (non-sensitive)</Label>
              <Textarea data-testid="assign-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" placeholder="No CAD, NCIC, medical, or personnel details." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="assign-save" onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ BULK ADD (weekly + monthly) ============ */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Add to Schedule</DialogTitle>
            <DialogDescription>
              {bulk?.mode === "month"
                ? `Schedule one person to a shift across several dates in ${monthName(anchor)} at once.`
                : "Schedule one person to a shift across several days in this week at once."}
            </DialogDescription>
          </DialogHeader>
          {bulk && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={labelCls}>Division</Label>
                  <Select value={bulk.division} onValueChange={(v) => setBulk({ ...bulk, division: v })}>
                    <SelectTrigger data-testid="bulk-division" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{manageableDivisions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelCls}>Shift</Label>
                  <Select value={bulk.shift_name} onValueChange={setBulkShift}>
                    <SelectTrigger data-testid="bulk-shift" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{shifts.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className={labelCls}>Employee (or Open)</Label>
                <Select value={bulk.employee_id || "open"} onValueChange={setBulkEmployee}>
                  <SelectTrigger data-testid="bulk-employee" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open position (unassigned)</SelectItem>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>{bulk.mode === "month" ? `Dates in ${monthName(anchor)}` : "Days this week"}</Label>
                {bulk.mode === "month" ? (
                  <>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="bulk-shortcuts">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((lbl, idx) => (
                        <button key={lbl} type="button" data-testid={`bulk-shortcut-${lbl.toLowerCase()}`}
                          onClick={() => selectWeekday(idx)}
                          className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-700">
                          All {lbl}
                        </button>
                      ))}
                      <button type="button" data-testid="bulk-shortcut-weekdays" onClick={() => selectWeekday("weekdays")}
                        className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100">Weekdays</button>
                      <button type="button" data-testid="bulk-shortcut-clear" onClick={clearBulkDays}
                        className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:border-red-300 hover:text-red-600">Clear</button>
                    </div>
                    <div className="mt-2 rounded-md border border-slate-200 p-2">
                      <div className="grid grid-cols-7 gap-1">
                        {WEEKDAYS.map((d) => <div key={d} className="py-1 text-center text-[10px] font-bold uppercase text-slate-400">{d}</div>)}
                        {monthCells.map((cell) => {
                          if (!cell.inMonth) return <div key={cell.key} />;
                          const on = !!bulk.days[cell.key];
                          return (
                            <button key={cell.key} type="button" data-testid={`bulk-day-${cell.key}`}
                              onClick={() => toggleBulkDay(cell.key)}
                              className={`flex aspect-square items-center justify-center rounded text-xs font-medium transition-colors ${on ? "bg-blue-600 text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`}>
                              {cell.day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-7" data-testid="bulk-days">
                    {weekKeys.map((key) => {
                      const on = !!bulk.days[key];
                      const d = parseKey(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`bulk-day-${key}`}
                          onClick={() => toggleBulkDay(key)}
                          className={`flex flex-col items-center rounded-md border px-1 py-2 text-xs transition-colors ${on ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                        >
                          <span className="font-semibold uppercase">{WEEKDAYS[d.getDay()]}</span>
                          <span className="text-[11px]">{d.getDate()}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={labelCls}>Start time</Label>
                  <Input data-testid="bulk-start" type="time" value={bulk.start_time} onChange={(e) => setBulk({ ...bulk, start_time: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className={labelCls}>End time</Label>
                  <Input data-testid="bulk-end" type="time" value={bulk.end_time} onChange={(e) => setBulk({ ...bulk, end_time: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <p className="-mt-1 text-xs text-slate-400" data-testid="bulk-tz-note">{tzBanner(tz)} · Auto-filled from the shift; edit if needed. End before start = overnight.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={labelCls}>Rank / Role</Label>
                  <Input data-testid="bulk-rank" value={bulk.rank_role} onChange={(e) => setBulk({ ...bulk, rank_role: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className={labelCls}>Status</Label>
                  <Select value={bulk.status} onValueChange={(v) => setBulk({ ...bulk, status: v })}>
                    <SelectTrigger data-testid="bulk-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs" data-testid="bulk-summary">
            <span className="font-semibold text-slate-700">
              {bulkNewCount > 0 ? `${bulkNewCount} assignment${bulkNewCount === 1 ? "" : "s"} will be created` : "No new dates selected"}
            </span>
            {bulkDupCount > 0 && <span className="text-amber-600">{bulkDupCount} already scheduled — will be skipped</span>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button data-testid="bulk-submit" disabled={bulkBusy || bulkNewCount <= 0} onClick={submitBulk} className="bg-blue-600 hover:bg-blue-700 text-white">
              {bulkBusy ? "Adding…" : `Add to Schedule${bulkNewCount > 0 ? ` (${bulkNewCount})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ COPY PREVIOUS WEEK ============ */}
      <AlertDialog open={copyOpen} onOpenChange={setCopyOpen}>
        <AlertDialogContent data-testid="copy-week-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Copy previous week?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>This copies every shift you manage from the previous week into the week you're viewing.</p>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-700">
                  <p><span className="font-semibold">From:</span> {keyLabel(prevWeekKeys[0], { month: "long", day: "numeric" })} – {keyLabel(prevWeekKeys[6], { month: "long", day: "numeric" })} <span className="text-slate-400">({prevWeekCount} shift{prevWeekCount === 1 ? "" : "s"})</span></p>
                  <p className="mt-1"><span className="font-semibold">To:</span> {keyLabel(weekKeys[0], { month: "long", day: "numeric" })} – {keyLabel(weekKeys[6], { month: "long", day: "numeric" })}</p>
                </div>
                {destWeekCount > 0 && (
                  <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-800" data-testid="copy-week-conflict-warning">
                    This week already has {destWeekCount} shift{destWeekCount === 1 ? "" : "s"}. Existing entries will be kept and any duplicates skipped — nothing is overwritten.
                  </p>
                )}
                {prevWeekCount === 0 && (
                  <p className="rounded-md border border-slate-200 bg-white p-2 text-slate-500" data-testid="copy-week-empty-note">The previous week has no shifts you manage to copy.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="copy-week-confirm"
              disabled={copyBusy || prevWeekCount === 0}
              onClick={(e) => { e.preventDefault(); submitCopyWeek(); }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {copyBusy ? "Copying…" : "Skip conflicts & copy"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ============ COPY PREVIOUS MONTH ============ */}
      <Dialog open={copyMonthOpen} onOpenChange={setCopyMonthOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="copy-month-dialog">
          <DialogHeader>
            <DialogTitle>Copy previous month?</DialogTitle>
            <DialogDescription>
              Copies each shift you manage from {monthName(prevMonthAnchor)} into {monthName(anchor)}, preserving the weekday pattern (e.g. every Monday–Thursday). Existing entries are never overwritten.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-700">
              <p><span className="font-semibold">From:</span> {monthName(prevMonthAnchor)}</p>
              <p className="mt-1"><span className="font-semibold">To:</span> {monthName(anchor)}</p>
            </div>
            {!monthPreview && <p className="text-slate-400" data-testid="copy-month-loading">Loading preview…</p>}
            {monthPreview && monthPreview.error && <p className="text-red-500">Could not load preview.</p>}
            {monthPreview && !monthPreview.error && (
              <>
                <div className="flex flex-wrap gap-2 text-xs" data-testid="copy-month-counts">
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">{monthPreview.will_create} to create</span>
                  {monthPreview.conflicts > 0 && <span className="rounded-full bg-amber-50 px-2.5 py-1 font-semibold text-amber-700">{monthPreview.conflicts} conflict{monthPreview.conflicts === 1 ? "" : "s"} (skipped)</span>}
                  {monthPreview.no_match > 0 && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-500">{monthPreview.no_match} no matching date</span>}
                </div>
                {monthPreview.will_create === 0 && (
                  <p className="rounded-md border border-slate-200 bg-white p-2 text-slate-500" data-testid="copy-month-empty-note">
                    Nothing new to copy from {monthName(prevMonthAnchor)}.
                  </p>
                )}
                <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-1" data-testid="copy-month-preview-list">
                  {(monthPreview.preview || []).map((p, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-xs">
                      <span className="min-w-0 truncate text-slate-700">
                        <span className="font-medium">{p.employee_name}</span> · {p.shift_name}
                        <span className="text-slate-400"> · {p.source_date} → {p.dest_date || "—"}</span>
                      </span>
                      <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${p.status === "new" ? "bg-emerald-100 text-emerald-700" : p.status === "conflict" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                        {p.status === "new" ? "Create" : p.status === "conflict" ? "Conflict" : "No date"}
                      </span>
                    </div>
                  ))}
                  {(monthPreview.preview || []).length === 0 && <p className="px-2 py-3 text-slate-400">No shifts you manage in {monthName(prevMonthAnchor)}.</p>}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyMonthOpen(false)}>Cancel</Button>
            <Button
              data-testid="copy-month-confirm"
              disabled={copyMonthBusy || !monthPreview || monthPreview.error || (monthPreview.will_create || 0) === 0}
              onClick={submitCopyMonth}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {copyMonthBusy ? "Copying…" : `Skip conflicts & copy${monthPreview && monthPreview.will_create ? ` (${monthPreview.will_create})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ ADD / EDIT ABSENCE ============ */}
      <Dialog open={absOpen} onOpenChange={setAbsOpen}>
        <DialogContent data-testid="absence-dialog">
          <DialogHeader>
            <DialogTitle>{absEditing ? "Edit Absence" : "Add Absence"}</DialogTitle>
            <DialogDescription>Records who is unavailable — the scheduled shift is kept so you can still see the coverage gap.</DialogDescription>
          </DialogHeader>
          {absForm && (
            <div className="space-y-3">
              <div>
                <Label className={labelCls}>Employee</Label>
                <Select value={absForm.employee_id || ""} onValueChange={setAbsEmployee}>
                  <SelectTrigger data-testid="absence-employee" className="mt-1.5"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.primary_division ? ` · ${m.primary_division}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={labelCls}>Type</Label>
                  <Select value={absForm.type} onValueChange={(v) => setAbsForm({ ...absForm, type: v })}>
                    <SelectTrigger data-testid="absence-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{ABSENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelCls}>Division</Label>
                  <Select value={absForm.division} onValueChange={(v) => setAbsForm({ ...absForm, division: v })}>
                    <SelectTrigger data-testid="absence-division" className="mt-1.5"><SelectValue placeholder="Division" /></SelectTrigger>
                    <SelectContent>{manageableDivisions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={labelCls}>Start date</Label>
                  <Input data-testid="absence-start" type="date" value={absForm.start_date} onChange={(e) => setAbsForm({ ...absForm, start_date: e.target.value, end_date: absForm.end_date < e.target.value ? e.target.value : absForm.end_date })} className="mt-1.5" />
                </div>
                <div>
                  <Label className={labelCls}>End date</Label>
                  <Input data-testid="absence-end" type="date" value={absForm.end_date} onChange={(e) => setAbsForm({ ...absForm, end_date: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label className={labelCls}>Notes (optional)</Label>
                <Input data-testid="absence-notes" value={absForm.notes || ""} onChange={(e) => setAbsForm({ ...absForm, notes: e.target.value })} className="mt-1.5" placeholder="e.g. called in at 6am" />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            {absEditing ? (
              <Button data-testid="absence-delete" variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" onClick={removeAbsence}>
                <TrashIcon className="h-4 w-4" /> Remove
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAbsOpen(false)}>Cancel</Button>
              <Button data-testid="absence-save" disabled={absBusy} onClick={submitAbsence} className="bg-blue-600 hover:bg-blue-700 text-white">{absBusy ? "Saving…" : "Save"}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ PRINT-ONLY CALENDAR (portaled to <body> so print hides everything else) ============ */}
      {createPortal(
      <div
        id="schedule-print"
        data-testid="schedule-print"
        className={view === "monthly" ? `weeks-${monthPrintMeta.weeks} ${monthPrintMeta.density}` : ""}
      >
        <div className="print-cal-head">
          <div className="print-head-main">
            <div className="print-cal-kicker">WatchRelay — Shift Schedule</div>
            <div className="print-cal-org">
              {user?.org_name || "Organization"}
              {filters.division !== "all" ? ` — ${filters.division}` : " — All Divisions"}
            </div>
            <div className="print-cal-period">
              {view === "monthly"
                ? monthName(anchor)
                : `${keyLabel(weekKeys[0], { month: "long", day: "numeric" })} – ${keyLabel(weekKeys[6], { month: "long", day: "numeric" })}`}
            </div>
          </div>
          <div className="print-head-side">{tzBanner(tz)}</div>
        </div>

        {view === "monthly" ? (
          <div className="print-cal-grid print-month-grid">
            {WEEKDAYS.map((d) => <div key={d} className="print-cal-dow">{d}</div>)}
            {monthCells.map((cell) => {
              const groups = cell.inMonth ? printGroups(byDate[cell.key]) : [];
              const offOnly = cell.inMonth && showAbsences
                ? absencesForDate(cell.key).filter((ab) => !(byDate[cell.key] || []).some((a) => (ab.employee_id && a.employee_id === ab.employee_id) || a.employee_name === ab.employee_name))
                : [];
              return (
                <div key={cell.key} className={`print-cal-cell${cell.inMonth ? "" : " out"}`}>
                  <div className="print-cal-date">{cell.day}</div>
                  <div className="print-cal-body">
                    {groups.map((g) => (
                      <div key={g.shiftName} className="print-grp">
                        <div className="print-grp-label">{g.label}</div>
                        {g.standard.length > 0 && (
                          <div className="print-grp-emps">
                            {g.standard.map((a, i) => {
                              const ab = showAbsences ? absenceForEmpDate(a, cell.key) : null;
                              const rc = roleCodeOf(a);
                              return (
                                <span key={i} className="print-emp">
                                  {abbrevOf(a)}
                                  {rc ? <span className="print-role">{rc}</span> : null}
                                  {ab ? <span className="print-abs">[{absCode(ab.type)}]</span> : null}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {g.exceptions.map((a) => {
                          const ab = showAbsences ? absenceForEmpDate(a, cell.key) : null;
                          const rc = roleCodeOf(a);
                          return (
                            <div key={a.id} className="print-grp-emps">
                              <span className="print-emp">
                                {abbrevOf(a)}
                                {rc ? <span className="print-role">{rc}</span> : null}
                                {ab ? <span className="print-abs">[{absCode(ab.type)}]</span> : null}
                              </span>
                              <span className="print-exc-time"> — {compactRange(a, tz).toUpperCase()}</span>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    {offOnly.length > 0 && (
                      <div className="print-grp">
                        <div className="print-grp-label off">OFF / LEAVE</div>
                        <div className="print-grp-emps">
                          {offOnly.map((ab, i) => (
                            <span key={ab.id} className="print-emp">
                              {ab.employee_name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                              <span className="print-abs">[{absCode(ab.type)}]</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="print-cal-grid print-week-grid">
            {weekKeys.map((key) => (
              <div key={`h-${key}`} className="print-cal-dow">
                {keyLabel(key, { weekday: "short" })} {parseKey(key).getDate()}
              </div>
            ))}
            {weekKeys.map((key) => (
              <div key={key} className="print-cal-cell">
                {shiftGroups(byDate[key]).flatMap(([, list]) => list).map((a) => {
                  const ab = showAbsences ? absenceForEmpDate(a, key) : null;
                  const rc = roleCodeOf(a);
                  return (
                  <div key={a.id} className="print-entry">
                    <span className="nm">{a.employee_name || "Open"}{rc ? ` · ${rc}` : ""}{ab ? ` · ${ab.type}` : ""}</span>
                    <span className="mt">{a.shift_name} · {fmtTime(a.start, tz)}–{fmtTime(a.end, tz)}</span>
                    {a.rank_role ? <span className="mt sub">{a.division} · {a.rank_role}</span> : null}
                  </div>
                  );
                })}
                {showAbsences && absencesForDate(key)
                  .filter((ab) => !(byDate[key] || []).some((a) => (ab.employee_id && a.employee_id === ab.employee_id) || a.employee_name === ab.employee_name))
                  .map((ab) => (
                    <div key={ab.id} className="print-entry"><span className="nm">{ab.employee_name} — {ab.type}</span></div>
                  ))}
              </div>
            ))}
          </div>
        )}

        <div className="print-legend" data-testid="print-absence-legend">
          {showAbsences && (
            <div className="print-legend-line">
              <span className="print-legend-title">Absence:</span>{" "}
              V Vacation · C Call-In · H Holiday · S Sick · T Training · P Personal · O Other
            </div>
          )}
          {rolesInUse.length > 0 && (
            <div className="print-legend-line" data-testid="print-roles-legend">
              <span className="print-legend-title">Roles:</span>{" "}
              {rolesInUse.map((r, i) => (
                <span key={r.id}>{i > 0 ? " · " : ""}{r.short_code} {r.name}</span>
              ))}
            </div>
          )}
        </div>
      </div>,
      document.body)}
    </div>
  );
}

function WeekRowCard({ a, onClick, tz }) {
  return (
    <button data-testid={`assignment-${a.id}`} onClick={onClick} className="flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-left shadow-sm hover:border-blue-300">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-800">{a.employee_name || "Open position"}</p>
        <p className="truncate text-xs text-slate-500">{a.division} · {a.shift_name} · {fmtTime(a.start, tz)}–{fmtTime(a.end, tz)}</p>
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLOR[a.status] || "bg-slate-100 text-slate-600"}`}>{a.status}</span>
    </button>
  );
}
